import { generatePath, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Block from 'components/block'
import ButtonV2 from 'components/button-v2'
import { Horizontal } from 'components/layout'
import { MoreLink } from 'components/link'
import { SectionTitle, SubSectionTitle } from 'components/typography'
import ROUTING_PATH from 'constants/route'
import useDisclosure from 'hooks/useDisclosure'
import { UserRole } from 'schema/user'
import SettingDialog from '../../../features/contracts/dialog/setting-dialog'
import ActiveInventoryList from '../../../features/contracts/list/active-inventory-list'
import SuccessfullyConcludedDealList from '../../../features/contracts/list/successfully-concluded-deal-list'
import {
  ActiveInventoryConfig,
  Contract,
  SuccessfullyConcludedDealConfig,
} from '../../../schema/contract'
import { Wrapper } from '../detail'
import { useIsSp } from 'hooks'
import theme from 'styles/theme'
import { StrongText } from '../../../components/typography'

const SectionHeader = styled(Horizontal)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`

const SubSectionHeader = styled(Horizontal)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    margin-bottom: 0px;
  }
`

const HorizontalScrollWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: ${(p) => p.theme.space.l};

  > a + a,
  > div + div {
    margin-left: 20px;
  }

  > a:nth-child(3n) + a,
  > div:nth-child(3n) + div {
    margin-left: 0px;
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    /* 横スクロール用スタイル */
    margin-right: -${(p) => p.theme.space.l};
    margin-left: 0;
    padding-right: ${(p) => p.theme.space.l};

    margin-bottom: 0px;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: scroll;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
    gap: ${(p) => p.theme.space.s};

    > a,
    > div {
      flex-shrink: 0;
    }

    > a + a,
    > div + div {
      margin-left: 0;
      margin-top: 0;
    }
  }
`

const OnlyPc = styled.div`
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    display: none;
  }
`
const OnlySp = styled.div`
  @media only screen and (min-width: ${(p) => p.theme.vp + 1}px) {
    display: none;
  }
`

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.space.l};
`

interface PropertyExampleInfoAreaProps {
  contractId: string
  config: ActiveInventoryConfig | SuccessfullyConcludedDealConfig | undefined
  contract: Contract
  userRole: UserRole | null
  isOwner: boolean
  onSuccess: () => void
  reFetch: () => void
}

const PropertyExampleInfoArea = ({
  contractId,
  config,
  contract,
  userRole,
  isOwner,
  onSuccess,
  reFetch,
}: PropertyExampleInfoAreaProps) => {
  const {
    isOpen: isDialogOpen,
    open: openDialog,
    close: closeDialog,
  } = useDisclosure()

  const navigate = useNavigate()
  const isSp = useIsSp(theme.vp)
  const handleDialogSuccess = () => {
    closeDialog()
    onSuccess()
  }

  const handleActiveInventoryMapClick = () => {
    const mapPath = generatePath(
      userRole === UserRole.OWNER
        ? ROUTING_PATH.OwnerActiveInventoriesMap
        : ROUTING_PATH.AgentActiveInventoriesMap,
      { id: contractId }
    )
    navigate(mapPath)
  }

  const handleSuccessfullyConcludedDealMapClick = () => {
    const mapPath = generatePath(
      userRole === UserRole.OWNER
        ? ROUTING_PATH.OwnerSuccessfullyConcludedDealsMap
        : ROUTING_PATH.AgentSuccessfullyConcludedDealsMap,
      { id: contractId }
    )
    navigate(mapPath)
  }

  const ActiveInventoryArea = () => {
    const activeInventoryItems = contract?.activeInventory?.items ?? []

    if (activeInventoryItems.length === 0) {
      return <></>
    }

    const to =
      activeInventoryItems.length === 0
        ? undefined
        : generatePath(
            userRole === UserRole.OWNER
              ? ROUTING_PATH.OwnerActiveInventories
              : ROUTING_PATH.AgentActiveInventories,
            { id: contract.id }
          )

    return (
      <Wrapper hideContainer={isSp}>
        <SubSectionHeader>
          <SubSectionTitle>周辺の売出物件</SubSectionTitle>
          <ButtonV2
            variant="secondary"
            size="sm"
            onClick={handleActiveInventoryMapClick}
          >
            <img src="/image/map.svg" alt="地図" width="16" height="16" />
            地図で見る
          </ButtonV2>
        </SubSectionHeader>
        <HorizontalScrollWrapper>
          <ActiveInventoryList
            list={activeInventoryItems.slice(0, 3)}
            contractId={contractId}
            isOwner={isOwner}
            reFetch={reFetch}
            background={isSp ? 'white' : 'gray'}
          />
        </HorizontalScrollWrapper>
        {!!to && <MoreLink to={to} />}
      </Wrapper>
    )
  }

  const SuccessfullyConcludedDealArea = () => {
    const items = contract?.successfullyConcludedDeal?.items ?? []

    if (items.length === 0) {
      return <></>
    }

    const to =
      items.length === 0
        ? undefined
        : generatePath(
            userRole === UserRole.OWNER
              ? ROUTING_PATH.OwnerSuccessfullyConcludedDeals
              : ROUTING_PATH.AgentSuccessfullyConcludedDeals,
            { id: contract.id }
          )

    return (
      <Wrapper hideContainer={isSp}>
        <SubSectionHeader>
          <SubSectionTitle>周辺の成約事例</SubSectionTitle>
          <ButtonV2
            variant="secondary"
            size="sm"
            onClick={handleSuccessfullyConcludedDealMapClick}
          >
            <img src="/image/map.svg" alt="地図" width="16" height="16" />
            地図で見る
          </ButtonV2>
        </SubSectionHeader>
        <HorizontalScrollWrapper>
          <SuccessfullyConcludedDealList
            list={items.slice(0, 3)}
            background={isSp ? 'white' : 'gray'}
          />
        </HorizontalScrollWrapper>
        {!!to && <MoreLink to={to} />}
      </Wrapper>
    )
  }

  return (
    <Block>
      <SectionHeader>
        <SectionTitle marginBottom="0">周辺の売出物件と成約事例</SectionTitle>
        {!isOwner && (
          <OnlyPc>
            <ButtonV2 variant="secondary" size="lg" onClick={openDialog}>
              <img src="/image/setting.svg" alt="設定" width="24" height="24" />
              表示条件を設定
            </ButtonV2>
          </OnlyPc>
        )}
      </SectionHeader>
      <SettingDialog
        contractId={contractId}
        config={config}
        showDialog={isDialogOpen}
        onCancel={closeDialog}
        onSuccess={handleDialogSuccess}
      />
      <FlexColumn>
        <ActiveInventoryArea />
        <SuccessfullyConcludedDealArea />
      </FlexColumn>

      {!isOwner && (
        <OnlySp>
          <div style={{ marginTop: theme.space.xl }}>
            <ButtonV2 variant="secondary" size="full" onClick={openDialog}>
              <StrongText size="md">表示条件を設定</StrongText>
            </ButtonV2>
          </div>
        </OnlySp>
      )}
    </Block>
  )
}

export default PropertyExampleInfoArea
